import Yup, { object, ObjectShape, isSchema, string } from 'yup';

import { ResultOptions } from '@types';
import { rollbar, retryImport } from '@utils';

import { CloudFunctionsService } from '../CloudFunctions';
import { FirebaseService } from '../Firebase';

/**
 * Generates a Yup schema based on the provided subModules, translation function, and utility store.
 * @param subModules - An array of subModules.
 * @returns A Yup schema object.
 */

interface SubModuleMap {
  [key: string]: string;
}

export const subModuleMap: SubModuleMap = {
  SUB_NAME: 'name',
  SUB_DEBT_AMOUNT: 'debtAmount',
  SUB_STATE: 'state',
  SUB_DEBT_GOAL: 'debtGoal',
  SUB_ZIP_CODE: 'zipCode',
  SUB_COMPACT_CONTACT: 'compactContactInfo',
  SUB_CONTACT: 'contactInfo',
  SUB_DEBT_TYPES: 'debtTypes',
  SUB_PAYMENT_STATUS: 'paymentStatus',
  SUB_DEBT_CAUSE: 'debtCause',
  SUB_EMPLOYMENT_STATUS: 'employmentStatus',
  SUB_HOUSEHOLD_INCOME: 'householdIncome',
  SUB_INCOME_CONSISTENCY: 'incomeConsistency',
  SUB_CREDIT_SCORE: 'creditScore',
  SUB_CREDIT_SCORE_IMPORTANCE: 'creditScoreImportance',
  SUB_PROPERTY_TYPE: 'propertyType',
  SUB_FUTURE_LOAN_PLANS: 'futureLoanPlans',
};

type SchemaDefaultValues = {
  [key: string]: string | number | boolean | null | object | undefined;
};

export const getDefaultValuesFromSchema = (
  schema: Yup.ObjectSchema<SchemaDefaultValues>
): SchemaDefaultValues => {
  const defaultValues: SchemaDefaultValues = {};

  const schemaDescription = schema.describe();
  const fields = schemaDescription.fields;

  Object.keys(fields).forEach((key) => {
    const field = fields[key];

    if (field.type === 'string') {
      defaultValues[key] = '';
    } else if (field.type === 'number') {
      defaultValues[key] = 0;
    } else if (field.type === 'array') {
      defaultValues[key] = [];
    } else if (field.type === 'boolean') {
      defaultValues[key] = false;
    } else if (field.type === 'object') {
      defaultValues[key] = {};
    } else {
      defaultValues[key] = null;
    }
  });

  return defaultValues;
};

type ValidationSchemaResult = Yup.AnySchema | ObjectShape;
type ValidationModule = {
  getValidationSchema: () =>
    | ValidationSchemaResult
    | Promise<ValidationSchemaResult>;
};

// Every validation module except the two that pull in the 125 KB phone library ships
// in the main bundle, so the first step needs no extra round trips. The contact ones
// stay lazy and are retried once if their chunk fails to load.
const bundledValidations = import.meta.glob(
  [
    '../../components/subModules/*/*.validation.ts',
    '!../../components/subModules/{contactInfo,compactContactInfo}/*.validation.ts',
  ],
  { eager: true }
) as Record<string, ValidationModule>;

const lazyValidations = import.meta.glob(
  '../../components/subModules/{contactInfo,compactContactInfo}/*.validation.ts'
) as Record<string, () => Promise<ValidationModule>>;

const loadValidation = (name: string): Promise<ValidationModule> => {
  const key = `../../components/subModules/${name}/${name}.validation.ts`;
  const bundled = bundledValidations[key];
  if (bundled) return Promise.resolve(bundled);
  const lazy = lazyValidations[key];
  if (!lazy)
    return Promise.reject(new Error(`Unknown validation module "${name}"`));
  return retryImport(lazy);
};

export const generateYupSchema = async (subModules: string[]) => {
  const schemaTree: ObjectShape = {};
  const firebaseService = new FirebaseService(new CloudFunctionsService());

  const validSubModules = subModules.filter((sm) => sm && subModuleMap[sm]);

  // Load all modules + feature flags in parallel
  const [featureFlags, ...modules] = await Promise.all([
    // EC-1303: a failed flag fetch must not kill the schema (dead Continue).
    firebaseService.getFirebaseRemoteConfig('feature_flags').catch((error) => {
      rollbar.error('Failed to load feature flags for schema:', error as Error);
      return null;
    }),
    ...validSubModules.map((sm) =>
      loadValidation(subModuleMap[sm]).catch((error) => {
        rollbar.error(`Failed to load validation for ${sm}:`, error as Error);
        return null;
      })
    ),
  ]);

  // Resolve all schemas in parallel; await inside try so async rejections
  // (e.g. debtAmount's app_config fetch) are caught too (EC-1303).
  const schemas = await Promise.all(
    modules.map(async (m) => {
      try {
        return (await m?.getValidationSchema?.()) ?? null;
      } catch {
        return null;
      }
    })
  );

  // Build schemaTree from results
  validSubModules.forEach((subModule, i) => {
    const schema = schemas[i];
    if (!schema || typeof schema !== 'object') return;

    const fieldKey = subModuleMap[subModule];

    if (isSchema(schema)) {
      if (fieldKey === 'state') {
        // State/zip replacement handled below after loop
        if (!featureFlags?.enableZipCodeField) {
          schemaTree[fieldKey] = schema;
        }
      } else {
        schemaTree[fieldKey] = schema;
      }
    } else {
      // Not a single schema: a map of field -> schema (isSchema does not narrow the union).
      const shape = schema as ObjectShape;
      for (const key in shape) {
        if (Object.prototype.hasOwnProperty.call(shape, key)) {
          schemaTree[key] = shape[key];
        }
      }
    }
  });

  // If zipCode should replace state, load it
  if (
    featureFlags?.enableZipCodeField &&
    validSubModules.some((sm) => subModuleMap[sm] === 'state')
  ) {
    try {
      const moduleZipCode = await loadValidation('zipCode');
      schemaTree['zipCode'] =
        (await moduleZipCode.getValidationSchema()) as Yup.AnySchema;
    } catch (error) {
      rollbar.error('Failed to load zipCode validation:', error as Error);
    }
  }

  schemaTree['leadType'] = string<ResultOptions['leadType']>();

  return object().shape(schemaTree);
};
